import { useMonarchRule } from "@/monarch/rules/MonarchRulesProvider";
import {
  UseYourLearning,
  YourLearningTestData,
  YourLearningVariants,
} from "./types";

const CUSTOM_RULE_ID = "authXForYouYourLearning";

const defaultData: YourLearningTestData = {
  enabled: false,
  headerText: "Your learning",
  subheaderEnrolledText:
    "Pick up where you left off or explore your upgrade options to take advantage of edX's full range of benefits.",
  subheaderUnenrolledText:
    "It looks like you haven’t enrolled in any learning opportunities yet.",
  enrolled: {
    enabled: true,
    headerText: "Jump back into your learning",
    subheaderText:
      "Pick up right where you left off or start learning something new.",
    ctaText: "Continue learning",
    ctaLink: "https://home.edx.org",
  },
  unenrolled: {
    enabled: true,
    headerText: "Start your learning today",
    subheaderText: "Explore courses and programs built around your goals.",
    primaryCtaText: "Back to For You",
    primaryCtaLink: "#authx-for-you-top",
    secondaryCtaText: "Find your path",
    secondaryCtaLink: "#fyp",
  },
  upsell: {
    enabled: false,
    maxItems: 5,
    headerText: "Earn a Certificate",
    subheaderText:
      "Upgrade today for unlimited access, graded assignments, and a verified certificate of completion from the institution.",
    upgradeCtaText: "Earn a Certificate",
    enrolledBadge: {
      visible: true,
      text: "Enrolled",
    },
  },
};

const useYourLearning: UseYourLearning = () => {
  const { value, isLoading } = useMonarchRule<{
    data: Partial<YourLearningTestData>;
  }>(CUSTOM_RULE_ID, {});

  if (isLoading || !value) {
    return {
      isLoading,
      variant: "control",
      data: defaultData,
    };
  }

  const data = value.data ?? {};
  const variant: YourLearningVariants = data.enabled ? "test" : "control";

  return {
    isLoading,
    variant,
    data: {
      ...defaultData,
      ...data,
      enrolled: {
        ...defaultData.enrolled,
        ...(data.enrolled ?? {}),
      },
      unenrolled: {
        ...defaultData.unenrolled,
        ...(data.unenrolled ?? {}),
      },
      upsell: {
        ...defaultData.upsell,
        ...(data.upsell ?? {}),
        enrolledBadge: {
          ...defaultData.upsell.enrolledBadge,
          ...(data.upsell?.enrolledBadge ?? {}),
        },
      },
    },
  };
};

export default useYourLearning;
